import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Link2, ExternalLink } from 'lucide-react'
import { useQueries } from '@tanstack/react-query'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getCustomerMailers } from '@/modules/Customer/services'
import { getService } from '@/modules/Service/services'
import type { CustomerMailer, MailerType } from '@/modules/Customer/types'
import Paper from '@/components/Paper'

interface Props {
    customerId?: number
}

// "Bound services" surface for the report detail view. A report's
// delivery list is driven by the customer's `service.report` mailer,
// and that mailer binds to one or more services via `service_ids[]`.
// This block exposes that binding so an operator can see — at a
// glance — which services trigger a report's send-out.
//
// Source chain:
//   1. getCustomerMailers(customerId)
//   2. filter mailer_types has scope `service.report`
//   3. union each matching mailer's `service_ids`
//   4. getService(id) per unique service_id for display name
//
// Empty states:
//   - customer not loaded yet → renders nothing (parent shows its own loader)
//   - customer has no service.report mailer → "not configured" note
//   - mailer(s) exist but `service_ids` empty → "not bound to any service" note
const BoundServicesBlock = ({ customerId }: Props) => {
    const { t } = useTranslation('report_waf')

    const { data: mailersData, isLoading: mailersLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.REPORT_WAF, 'bound-mailers', customerId],
            queryFn: () => getCustomerMailers({ customerID: customerId! }),
            enabled: !!customerId,
        })

    const serviceIds = useMemo<number[]>(() => {
        const mailers: CustomerMailer[] = mailersData?.results || []
        const reportMailers = mailers.filter((m) =>
            m.mailer_types?.some(
                (mt: MailerType) => mt.mailer_scope === 'service.report'
            )
        )
        const ids = new Set<number>()
        reportMailers.forEach((m) =>
            (m.service_ids || []).forEach((id: any) => {
                const n = typeof id === 'number' ? id : Number(id)
                if (!Number.isNaN(n) && n > 0) ids.add(n)
            })
        )
        return [...ids]
    }, [mailersData])

    // Fan out one lookup per service_id. React Query dedupes and caches
    // so re-renders don't re-fire requests.
    const serviceQueries = useQueries({
        queries: serviceIds.map((id) => ({
            queryKey: ['service', id],
            queryFn: () => getService(id),
            enabled: !!id,
            // Service names rarely change — 5 min stale is plenty.
            staleTime: 5 * 60 * 1000,
        })),
    })

    const servicesResolved = serviceQueries
        .map((q, idx) => ({
            id: serviceIds[idx],
            name: q.data?.service_name,
            loaded: !q.isLoading,
        }))
        .filter((s) => !!s.id)

    if (!customerId) return null

    const hasReportMailer = (mailersData?.results || []).some(
        (m: CustomerMailer) =>
            m.mailer_types?.some(
                (mt: MailerType) => mt.mailer_scope === 'service.report'
            )
    )

    return (
        <Paper className="p-4 my-2">
            <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4" />
                <span className="text-sm font-semibold">
                    {t('bound_services.title', {
                        defaultValue: 'Bound services',
                    })}
                </span>
                <span className="text-xs text-muted-foreground">
                    {t('bound_services.subtitle', {
                        defaultValue:
                            'via the customer\u2019s service.report mailer',
                    })}
                </span>
            </div>

            {mailersLoading ? (
                <p className="text-xs text-muted-foreground">
                    {t('common.loading', { defaultValue: 'Loading…' })}
                </p>
            ) : !hasReportMailer ? (
                <p className="text-xs text-muted-foreground">
                    {t('bound_services.no_mailer', {
                        defaultValue:
                            'No service.report mailer configured for this customer — set one up to start delivering reports.',
                    })}
                </p>
            ) : servicesResolved.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                    {t('bound_services.no_services', {
                        defaultValue:
                            'Mailer exists but is not bound to any service.',
                    })}
                </p>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {servicesResolved.map((s) => (
                        <Link
                            key={s.id}
                            to={`/services/${s.id}`}
                            className="inline-flex items-center gap-1 rounded border border-primary/20 bg-primary/10 px-2 py-0.5 text-xs text-primary hover:bg-primary/20 transition-colors"
                        >
                            {s.name || `#${s.id}`}
                            <ExternalLink className="w-3 h-3 opacity-70" />
                        </Link>
                    ))}
                </div>
            )}
        </Paper>
    )
}

export default BoundServicesBlock
